import { useQuery } from '@tanstack/react-query'
import { useFetchWithAuth } from '../session/useFetchWithAuth'

const API_URL = import.meta.env.VITE_EPC_API_URL ?? ''

interface SearchResult {
  profiles: {
    nodes: Array<{
      id: string
      firstName?: string
      lastName?: string
      role?: string
      schoolName?: string
      companyName?: string
      color?: string
      avatarUrl?: string
    }>
  }
  events: {
    nodes: Array<{
      id: string
      name?: string
      description?: string
      color?: string
      iconUrl?: string
    }>
  }
}

interface GlobalSearchParams {
  query: string
  limit?: number
  isAdminContext?: boolean
}

/**
 * Hook to perform global search across profiles and events
 */
export const useGlobalSearch = ({
  query,
  limit = 9,
  isAdminContext = false,
}: GlobalSearchParams) => {
  const fetchWithAuth = useFetchWithAuth()

  return useQuery<{ search: SearchResult }>({
    queryKey: ['globalSearch', query, limit, isAdminContext],
    queryFn: async () => {
      if (!query.trim()) {
        return {
          search: {
            profiles: { nodes: [] },
            events: { nodes: [] },
          },
        }
      }

      // Add logging to compare search contexts
      const profileSearchUrl = `${API_URL}/profiles/search?query=${encodeURIComponent(
        query
      )}&limit=${limit}${isAdminContext ? '&admin_search=true' : ''}`

      console.log('Global search params:', {
        query,
        limit,
        isAdminContext,
        endpoint: profileSearchUrl,
      })

      // Perform parallel searches for profiles and events
      const [profilesResponse, eventsResponse] = await Promise.all([
        // Search profiles
        fetchWithAuth(profileSearchUrl, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
        }).catch((error) => {
          console.error('Profile search error:', error)
          return { items: [] }
        }),

        // Search events
        fetchWithAuth(
          `${API_URL}/events/search?query=${encodeURIComponent(
            query
          )}&limit=${limit}`,
          {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' },
          }
        ).catch((error) => {
          console.error('Event search error:', error)
          return { items: [] }
        }),
      ])

      // Transform API response to match expected format
      const profiles = profilesResponse?.items || []
      const events = eventsResponse?.items || []

      console.log('Profile search response:', profilesResponse)
      console.log('Event search response:', eventsResponse)
      console.log('Transformed profiles:', profiles.length)
      console.log('Transformed events:', events.length)

      return {
        search: {
          profiles: {
            nodes: profiles.map((profile: any) => ({
              id: profile.objectID || profile.id,
              firstName: profile.firstName,
              lastName: profile.lastName,
              role: profile.role,
              schoolName: profile.schoolName,
              companyName: profile.companyName,
              color: profile.color,
              avatarUrl: profile.avatarUrl,
            })),
          },
          events: {
            nodes: events.map((event: any) => ({
              id: event.objectID || event.id,
              name: event.name,
              description: event.description,
              color: event.color,
              iconUrl: event.iconUrl,
            })),
          },
        },
      }
    },
    enabled: !!query.trim(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}
